/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { I18n } from '@coze-arch/i18n';
import { Modal, Spin, Toast } from '@coze-arch/coze-design';
import { isApiError } from '@coze-arch/bot-http';

import {
  PERMISSION_SPECIFIC_ORG,
  SCOPE_SPECIFIC_ORG_EMPTY_MSG,
} from '../../constants/permission-scope';
import {
  getPublishConfig,
  updatePublishedAppScope,
  type PublishConfigResult,
} from '../../api/published-apps';
import {
  PublishScopeSelector,
  createDefaultPublishScopeValue,
  publishScopeValueFromConfig,
  type PublishScopeValue,
} from './publish-scope-selector';
import { PublishModalFooter } from './publish-modal-footer';
import { DeptTreeModal, type DeptTreeModalRef } from './dept-tree-modal';

import styles from './app-store-scope-modal.module.less';

export interface AppStoreScopeModalOpenOptions {
  botId: string;
  title?: string;
  onSaved?: () => void;
}

export interface AppStoreScopeModalRef {
  open: (options: AppStoreScopeModalOpenOptions) => void;
}

function configToScopeValue(config: PublishConfigResult): PublishScopeValue {
  const orgs = (config.scope_depts ?? []).map(dept => ({
    departmentId: dept.id,
    departmentName: dept.name,
  }));
  return publishScopeValueFromConfig(
    config.scope_type,
    config.scope_dept_ids,
    orgs,
  );
}

export const AppStoreScopeModal = forwardRef<AppStoreScopeModalRef>(
  (_, ref) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [appId, setAppId] = useState(0);
    const [scopeValue, setScopeValue] = useState<PublishScopeValue>(
      createDefaultPublishScopeValue,
    );
    const deptModalRef = useRef<DeptTreeModalRef>(null);
    const onSavedRef = useRef<(() => void) | undefined>();

    useImperativeHandle(ref, () => ({
      open: (options: AppStoreScopeModalOpenOptions) => {
        setModalTitle(
          options.title ?? I18n.t('abr_app_store_scope_modal_title'),
        );
        onSavedRef.current = options.onSaved;
        setScopeValue(createDefaultPublishScopeValue());
        setVisible(true);
        void loadConfig(options.botId);
      },
    }));

    const loadConfig = async (botId: string) => {
      setLoading(true);
      setAppId(0);
      try {
        const result = await getPublishConfig(botId);
        if (!result.found || !result.id) {
          Toast.warning(I18n.t('abr_app_store_scope_load_failed'));
          setVisible(false);
          return;
        }
        setAppId(result.id);
        setScopeValue(configToScopeValue(result));
      } catch (error) {
        setVisible(false);
        const msg = isApiError(error)
          ? error.message
          : I18n.t('abr_app_store_scope_load_failed');
        Toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    const performSave = async () => {
      setSaving(true);
      try {
        const scopeType = String(scopeValue.scopeType || '0');
        await updatePublishedAppScope(appId, {
          scope_type: scopeType,
          scope_dept_ids:
            scopeType === PERMISSION_SPECIFIC_ORG
              ? scopeValue.appointData
              : undefined,
        });
        Toast.success(I18n.t('abr_app_store_scope_saved'));
        onSavedRef.current?.();
        setVisible(false);
      } catch (error) {
        const msg = isApiError(error)
          ? error.message
          : I18n.t('abr_app_store_scope_save_failed');
        Toast.error(msg);
      } finally {
        setSaving(false);
      }
    };

    const handleSave = () => {
      const scopeType = String(scopeValue.scopeType || '0');
      if (
        scopeType === PERMISSION_SPECIFIC_ORG &&
        scopeValue.appointData.length === 0
      ) {
        Toast.warning(SCOPE_SPECIFIC_ORG_EMPTY_MSG);
        return;
      }
      Modal.confirm({
        title: I18n.t('abr_scope_update_confirm_title'),
        content: I18n.t('abr_scope_update_confirm_content'),
        okText: I18n.t('Confirm'),
        cancelText: I18n.t('Cancel'),
        onOk: () => performSave(),
      });
    };

    let content: ReactNode = null;
    if (loading) {
      content = (
        <div className={styles.loading}>
          <Spin />
        </div>
      );
    } else {
      content = (
        <PublishScopeSelector
          value={scopeValue}
          onChange={setScopeValue}
          onOpenDept={() =>
            deptModalRef.current?.open({ values: scopeValue.appointData })
          }
        />
      );
    }

    return (
      <>
        <Modal
          title={modalTitle}
          visible={visible}
          width={560}
          footer={
            <PublishModalFooter
              cancelText={I18n.t('Cancel')}
              okText={I18n.t('Save')}
              okLoading={saving}
              onCancel={() => setVisible(false)}
              onOk={handleSave}
            />
          }
          onCancel={() => setVisible(false)}
        >
          <div className={styles.body}>{content}</div>
        </Modal>
        <DeptTreeModal
          ref={deptModalRef}
          onConfirm={(orgs, appointData) => {
            setScopeValue(prev => ({ ...prev, orgs, appointData }));
          }}
        />
      </>
    );
  },
);

AppStoreScopeModal.displayName = 'AppStoreScopeModal';
